<script setup lang="ts">
  import type { FormError } from '@nuxt/ui';
  import { LazyModalGiftPlayMoney } from '#components';
  import { LS_DISPLAY_NAME_PROMPT } from '#shared/const/cookies';
  import { MAX_USERNAME_LENGTH, rules } from '#shared/validation';
  import { useAppToast } from '~/composables/shared/useAppToast';
  import { stores } from '~/store';

  const { showSuccessToast, showErrorToast } = useAppToast();

  const state = ref({
    username: '',
  });

  const errorsLength = ref(0);
  const nameIsAvailable = ref(false);
  const nameCheckingIsPending = ref(false);
  const updatingProfileIsPending = ref(false);

  const checkedNames = new Map<string, boolean>();

  async function validate(): Promise<FormError[]> {
    const errors: FormError[] = [];

    const testName = rules.username.safeParse(state.value.username);
    if (testName.error) {
      errors.push({ name: 'username', message: testName.error.issues[0]?.message || '' });
    }

    if (state.value.username?.length > MAX_USERNAME_LENGTH) {
      errors.push({ name: 'username', message: `Must be at most ${MAX_USERNAME_LENGTH} characters` });
    }

    await checkUserNameAvailable();
    if (!nameIsAvailable.value) {
      errors.push({ name: 'username', message: 'This username is already taken' });
    }

    errorsLength.value = errors.length;

    return errors;
  }

  async function checkUserNameAvailable() {
    if (checkedNames.has(state.value.username)) {
      nameIsAvailable.value = checkedNames.get(state.value.username);
      return;
    }

    if (nameCheckingIsPending.value) return;

    nameCheckingIsPending.value = true;
    nameIsAvailable.value = await stores.user.checkUserNameAvailable(state.value.username);
    checkedNames.set(state.value.username, nameIsAvailable.value);
    nameCheckingIsPending.value = false;
  }

  async function onSubmit() {
    if (errorsLength.value > 0) return;
    if (updatingProfileIsPending.value) return;

    updatingProfileIsPending.value = true;
    const response = await stores.user.updateUserProfile({ displayName: state.value.username });
    updatingProfileIsPending.value = false;

    if (response.haserror) {
      showErrorToast('Error', response.status);
    } else {
      localStorage.removeItem(`${LS_DISPLAY_NAME_PROMPT}${stores.user.getUserId()}`);

      showSuccessToast('Success', response.status);

      stores.signUp.setSignUpInProcess(false);
      stores.modals.close('sign-up');

      stores.modals.open('gift-play-money', LazyModalGiftPlayMoney);
    }
  }

  onMounted(() => {
    if (stores.user.profileData.displayName) {
      state.value.username = stores.user.profileData.displayName;
      checkedNames.set(stores.user.profileData.displayName, true);
    }
  });
</script>

<template>
  <div class="flex flex-col gap-4 items-stretch">
    <h2 class="text-white text-center text-2xl font-semibold">Please enter your username:</h2>
    <UForm :state="state" :validate="validate" class="w-full flex flex-col gap-4" @submit="onSubmit">
      <button class="opacity-0 w-0 h-0 absolute" autofocus aria-hidden="true" />

      <UFormField
        :ui="{
          root: 'relative h-[66px]',
          label: 'text-white font-barlow text-sm font-medium leading-snug pb-1',
          error: 'mt-0.5 h-5',
        }"
        name="username"
        required
        eager-validation
      >
        <CoreInput v-model="state.username" :maxlength="MAX_USERNAME_LENGTH" variant="subtle">
          <template #trailing></template>
        </CoreInput>
        <!--        <p v-if="errorsLength === 0" class="text-sm text-error absolute bottom-0">Must be at least 4 characters</p> -->
      </UFormField>

      <CoreButton
        type="submit"
        :disabled="errorsLength > 0"
        :loading="nameCheckingIsPending || updatingProfileIsPending"
        :class="errorsLength === 0 ? '' : 'grayscale-100'"
      >
        Set
      </CoreButton>
    </UForm>
  </div>
</template>
